import { useEffect, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Box, CircularProgress } from '@mui/material'

import { Button, Stack, Text } from '../components'
import { mutations, queries } from '../graphql'

export function BankAccountSetup({
  onSuccess,
  setEnableManualOption,
  refetchGetZumRailsUser,
  setIsPaymentUILoading,
  hideText,
  onBack,
  refetchAccount,
  activeFundId = null,
  charityId = null,
}) {
  const apolloClient = useApolloClient()
  const [isProcessing, setIsProcessing] = useState(false)

  const { data: { userHasBankAccount } = {}, loading: userHasBankAccountLoading } = useQuery(
    queries.paymentMethods.userHasBankAccount,
    {
      variables: { fundId: activeFundId, charityId },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  )

  const { data: { getZumrailsToken: addPaymentProfileToken } = {}, refetch: refetchZumrailsToken } = useQuery(
    queries.paymentMethods.getZumrailsToken,
    {
      variables: { fundId: activeFundId, charityId },
      fetchPolicy: 'network-only',
    }
  )

  const [createZumRailsUser] = useMutation(mutations.paymentMethods.createZumRailsUser, {
    refetchQueries: [
      { query: queries.paymentMethods.userHasBankAccount, variables: { fundId: activeFundId, charityId } },
    ],
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    if (userHasBankAccount && !userHasBankAccountLoading && !isProcessing) {
      onSuccess?.()
    }
  }, [userHasBankAccount, userHasBankAccountLoading, isProcessing, onSuccess])

  useEffect(() => setIsProcessing(false), [])

  const handleRefetch = async () => {
    try {
      await apolloClient.refetchQueries({
        include: ['Charity', 'getCharityBankAccounts', 'userHasBankAccount'],
        updateCache: (cache) => {
          cache.evict({ fieldName: 'userHasBankAccount' })
          cache.evict({ fieldName: 'getCharityBankAccounts' })
        },
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error refetching queries:', error)
    }
  }

  const loadIframe = () => {
    const token = addPaymentProfileToken
    setIsProcessing(true)

    // eslint-disable-next-line no-undef
    ZumRailsSDK.init({
      token,
      onError: () => {
        setIsProcessing(false)
        setEnableManualOption?.(true)
      },
      onSuccess: async (data) => {
        try {
          await createZumRailsUser({
            variables: {
              data: {
                id: data.userId,
                fundId: activeFundId,
                charityId,
              },
            },
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating ZumRails user:', error)
        } finally {
          setIsProcessing(false)
          refetchAccount?.()
          await handleRefetch()
        }
      },
      onButtonClose: () => {
        setIsProcessing(false)
        refetchZumrailsToken()
        refetchGetZumRailsUser?.()
      },
    })
  }

  useEffect(() => {
    setIsPaymentUILoading?.(true)
  })

  const renderContent = () => {
    if (userHasBankAccountLoading || isProcessing) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>
              {isProcessing ? 'Setting up your bank account...' : 'Loading bank account information...'}
            </Text.Body>
          </div>
        </div>
      )
    }

    if (userHasBankAccount === undefined) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>Verifying bank account status...</Text.Body>
          </div>
        </div>
      )
    }

    return (
      <Stack spacing={2}>
        {!hideText && <Text.Body>To connect your online bank account, please click the button below.</Text.Body>}
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          {onBack && <Button onClick={() => onBack('')}>Back</Button>}
          <Button onClick={loadIframe} disabled={isProcessing}>
            Connect Bank Account
          </Button>
        </Box>
      </Stack>
    )
  }

  return <Stack spacing={4}>{renderContent()}</Stack>
}

export default BankAccountSetup
